import Papa from "papaparse";
import * as XLSX from "xlsx";

// Helper to parse DD/MM/YYYY
const parseDate = (dateStr) => {
  if (!dateStr) return null;
  const parts = dateStr.split("/");
  if (parts.length === 3) {
    // Note: Month is 0-indexed in JS
    return new Date(
      parseInt(parts[2]),
      parseInt(parts[1]) - 1,
      parseInt(parts[0])
    );
  }
  return null;
};

// Helper to calculate age from date string (Col X)
const calculateVehicleAge = (dateStr) => {
  const date = parseDate(dateStr);
  if (!date) return 5; // Default average age if missing
  const now = new Date();
  const age = now.getFullYear() - date.getFullYear();
  return Math.max(0, age);
};

// Logic to estimate Mileage based on Usage (Col Y), Type, and Age
const estimateAnnualMileage = (uso, tipo, age) => {
  const u = uso ? uso.toUpperCase() : "";
  const t = tipo ? tipo.toUpperCase() : "";

  let baseKm = 12000; // Default private car

  // 1. Distinction based on Usage (Col Y)
  if (
    u.includes("DI TERZI") ||
    u.includes("LOCAZIONE") ||
    u.includes("PUBBLICO")
  ) {
    // Professional Use
    if (t.includes("AUTOCARRO")) baseKm = 45000;
    else if (t.includes("AUTOBUS")) baseKm = 60000;
    else baseKm = 30000; // Taxi/NCC or Professional Car
  } else {
    // Private Use ("PROPRIO")
    if (t.includes("MOTO")) baseKm = 4000;
    else if (t.includes("AUTOCARRO")) baseKm = 15000; // Private truck
    else baseKm = 11000; // Private car avg
  }

  // 2. Adjustment based on Age
  // Older vehicles tend to be driven slightly less or are secondary cars
  if (age > 15) baseKm *= 0.6;
  else if (age > 8) baseKm *= 0.85;

  return Math.round(baseKm);
};

// Calculate exposure in years
const calculateExposure = (startStr, endStr) => {
  const start = parseDate(startStr);
  const end = parseDate(endStr);

  if (!start || !end) return 1.0; // Fallback

  const diffTime = Math.abs(end.getTime() - start.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  const exposure = diffDays / 365.0;

  return Math.max(0, exposure);
};

// Helper to normalize vehicle type from ANIA code/description
const normalizeVehicleType = (type) => {
  if (!type) return "Altro";
  const t = type.toUpperCase();
  if (t.includes("AUTOVETTURA")) return "Autovetture";
  if (t.includes("AUTOCARRO")) return "Autocarri";
  if (t.includes("RIMORCHIO") || t.includes("SEMIRIMORCHIO")) return "Rimorchi";
  if (t.includes("MOTO") || t.includes("CICLO")) return "Moto";
  if (t.includes("MACCHINA AGRICOLA") || t.includes("TRATTORE AGRICOLO"))
    return "Agricoli";
  if (t.includes("AUTOBUS")) return "Autobus";
  return "Altro";
};

// Helper to determine detailed vehicle type based on weight/CV and usage
const getDetailedVehicleType = (row, normType) => {
  if (normType === "Autovetture" || normType === "Moto") {
    const cvStr = row["Cavalli_fiscali"] || "0";
    const cv = parseFloat(cvStr.replace(",", "."));
    if (isNaN(cv) || cv === 0) return normType;
    const cvLabel = cv <= 18 ? "0-18 CV" : ">18 CV";
    return `${normType} ${cvLabel}`;
  }

  if (normType === "Autocarri" || normType === "Rimorchi") {
    const qliStr = row["PPC_Qli"] || "0";
    const qli = parseFloat(qliStr.replace(",", "."));

    let range = "";
    if (isNaN(qli) || qli === 0) range = "ND";
    else if (qli <= 35) range = "0-35ql";
    else if (qli <= 70) range = "36-70ql";
    else if (qli <= 120) range = "71-120ql";
    else if (qli <= 180) range = "121-180ql";
    else if (qli <= 360) range = "181-360ql";
    else range = ">360ql";

    const u = (row["Uso"] || "").toUpperCase();
    const usoLabel = u.includes("TERZI")
      ? "CT"
      : u.includes("PROPRIO")
      ? "CP"
      : "Altro";

    return `${normType} ${range} ${usoLabel}`;
  }

  return normType;
};

// --- NEW FORECAST ALGORITHM: Based on Yearly History ---
const forecastClaimsFromYearlyHistory = (
  yearlyData,
  futureMonths = 12,
  avgCost
) => {
  // Filter out years with 0 claims only if they look like outliers (keeping recent years even if low)
  const validData = yearlyData.filter((d) => d.claims > 0);

  if (validData.length === 0) return [];

  // Use last 3 valid years for average to be responsive but stable
  const recentData = validData.slice(-3);
  const avgClaims =
    recentData.reduce((sum, d) => sum + d.claims, 0) / recentData.length;

  // Calculate a simple trend from the last 5 years if available
  const trendData = validData.slice(-5);
  let slope = 0;
  if (trendData.length >= 2) {
    let sumX = 0,
      sumY = 0,
      sumXY = 0,
      sumXX = 0;
    trendData.forEach((d, i) => {
      sumX += i;
      sumY += d.claims;
      sumXY += i * d.claims;
      sumXX += i * i;
    });
    const n = trendData.length;
    slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
  }

  // Predict next year based on average + slight trend (dampened)
  const dampenedSlope = slope * 0.3; // Very conservative trend
  const nextYearProjection = avgClaims + dampenedSlope;

  // Clamp: Ensure it's within a reasonable range of the 3-year average (e.g. 50% - 150%)
  const min = avgClaims * 0.5;
  const max = avgClaims * 1.5;
  let finalYearlyProjection = Math.max(min, Math.min(max, nextYearProjection));

  if (finalYearlyProjection < 0) finalYearlyProjection = avgClaims;

  const monthlyProjection = finalYearlyProjection / 12;

  const forecast = [];
  const today = new Date();
  let currentYear = today.getFullYear();
  let currentMonth = today.getMonth() + 1;

  for (let i = 1; i <= futureMonths; i++) {
    let m = currentMonth + i;
    let y = currentYear;
    while (m > 12) {
      m -= 12;
      y++;
    }

    // Add slight random seasonality/noise (+/- 5%)
    const noise = Math.random() * 0.1 - 0.05;
    const val = monthlyProjection * (1 + noise);

    forecast.push({
      Mese: `${y}-${String(m).padStart(2, "0")}`,
      Sinistri_Previsti: parseFloat(val.toFixed(2)),
      Costo_Stimato: parseFloat((val * avgCost).toFixed(2)),
    });
  }

  return forecast;
};

// Helper to parse ATR codes (NA, ND, --, numbers)
const parseAtrValue = (val) => {
  if (!val) return null; // treated as NA
  const v = String(val).trim().toUpperCase();
  if (["NA", "ND", "--", "N.A.", "N.D."].includes(v)) return null; // Not observable

  // Try parsing number
  const num = parseInt(v);
  return isNaN(num) ? null : num;
};

// --- Excel Conversion Helper ---
export const convertExcelToCsv = async (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result);
        const workbook = XLSX.read(data, { type: "array" });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const csv = XLSX.utils.sheet_to_csv(worksheet, { FS: ";" });
        resolve(csv);
      } catch (error) {
        reject("Errore nella lettura del file Excel.");
      }
    };
    reader.onerror = () => reject("Errore caricamento file.");
    reader.readAsArrayBuffer(file);
  });
};

export const processCsvData = async (
  csvContent,
  avgCostSimulation,
  premiumOverrides = null,
  expenseRatioInput = 25, // General Expenses (Spese di Gestione)
  commissionRatioInput = 15, // Commissions (Provvigioni)
  deductibleInput = 0 // Franchigia (deducted from average cost)
) => {
  return new Promise((resolve, reject) => {
    Papa.parse(csvContent, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const rawData = results.data;

        if (rawData.length === 0) {
          reject("Nessun dato trovato nel CSV.");
          return;
        }

        // Extra distributions for ML context
        // Extra distributions for ML context
        const distProvincia = {};
        const distMarca = {};
        const kwValues = [];
        const ccValues = []; // Cilindrata (AJ)
        const vehicleAges = []; // Anzianità (X)
        const ageDistribution = {}; // Age buckets
        const bonusMalusDist = {}; // Classe (BE)
        const bonusMalusValues = [];
        const usageStats = { professionale: 0, privato: 0, altro: 0 };
        const mileageEstimates = [];
        const distCap = {}; // CAP (Col I)

        // NEW Distributions
        const distAlimentazione = {};
        const distBlackBox = {};
        const distCarrozzeria = {};
        const distModello = {};

        // Normalize and clean data
        const policies = rawData
          .map((row) => {
            // Priority: Totali_Principali_Paritari (Column BV) > Totale_Sinistri > 0
            let claimsCount = parseInt(
              row["Totali_Principali_Paritari"] || row["Totale_Sinistri"] || "0"
            );

            if (isNaN(claimsCount)) {
              claimsCount = 0;
            }

            const startDate =
              row["DataInizioCopertura"] || row["Data Decorrenza"] || "";
            const endDate =
              row["DataFineCopertura"] || row["Data Scadenza"] || "";
            const exposure = calculateExposure(startDate, endDate);

            const rawType =
              row["Tipo_veicolo_ania"] || row["TIPO_VEICOLO_ANIA"] || "";
            const type = normalizeVehicleType(rawType);
            const detailedType = getDetailedVehicleType(row, type);

            let premium = parseFloat(
              (row["Premio_Annuale"] || "500.0").replace(",", ".")
            );

            if (premiumOverrides) {
              if (premiumOverrides[detailedType] !== undefined) {
                premium = premiumOverrides[detailedType];
              } else if (premiumOverrides[type] !== undefined) {
                premium = premiumOverrides[type];
              }
            }

            // Cost Calculation with Deductible
            const effectiveCostPerClaim = Math.max(
              0,
              avgCostSimulation - deductibleInput
            );
            const usedCost = claimsCount * effectiveCostPerClaim;

            // --- ML DATA EXTRACTION ---
            const prov = row["ProvinciaResidenza"] || "ND";
            distProvincia[prov] = (distProvincia[prov] || 0) + 1;

            const marca = row["Marca"] || "ND";
            distMarca[marca] = (distMarca[marca] || 0) + 1;

            // CAP
            const cap = row["CAP"] || "ND";
            distCap[cap] = (distCap[cap] || 0) + 1;

            // Col AK: KW
            const kw = parseFloat((row["KW"] || "0").replace(",", "."));
            if (!isNaN(kw) && kw > 0) kwValues.push(kw);

            // Col AJ: Cilindrata
            const cc = parseFloat(
              (row["Cilindrata_cc"] || "0").replace(",", ".")
            );
            if (!isNaN(cc) && cc > 0) ccValues.push(cc);

            // Col X: Data Immatricolazione (Calculated Age)
            const immatrDate = row["Data_Immatricolazione"] || "";
            const age = calculateVehicleAge(immatrDate);
            vehicleAges.push(age);

            // Bucket age for distribution
            let ageBucket = ">15 Anni";
            if (age <= 2) ageBucket = "0-2 Anni";
            else if (age <= 5) ageBucket = "3-5 Anni";
            else if (age <= 10) ageBucket = "6-10 Anni";
            else if (age <= 15) ageBucket = "11-15 Anni";

            ageDistribution[ageBucket] = (ageDistribution[ageBucket] || 0) + 1;

            // Col BE: Classe Merito
            const bmClass = row["ClasseMerito"] || "ND";
            bonusMalusDist[bmClass] = (bonusMalusDist[bmClass] || 0) + 1;
            const bmNum = parseInt(bmClass);
            if (!isNaN(bmNum)) bonusMalusValues.push(bmNum);

            // Col Y: Uso
            const uso = row["Uso"] || "ND";
            if (uso.toUpperCase().includes("DI TERZI"))
              usageStats.professionale++;
            else if (uso.toUpperCase().includes("PROPRIO"))
              usageStats.privato++;
            else usageStats.altro++;

            // INFERRED METRIC: Mileage Estimation
            const estimatedKm = estimateAnnualMileage(uso, rawType, age);
            mileageEstimates.push(estimatedKm);

            // NEW FIELDS EXTRACTION
            const alimentazione = row["Tipo_alimentazione"] || "ND";
            distAlimentazione[alimentazione] =
              (distAlimentazione[alimentazione] || 0) + 1;

            const blackbox = row["Black_box"] || "ND";
            distBlackBox[blackbox] = (distBlackBox[blackbox] || 0) + 1;

            const carrozzeria = row["DescrizioneCarrozzeria"] || "ND";
            distCarrozzeria[carrozzeria] =
              (distCarrozzeria[carrozzeria] || 0) + 1;

            const modello = row["Modello"] || "";
            const fullModel = `${marca} ${modello}`.trim() || "ND";
            distModello[fullModel] = (distModello[fullModel] || 0) + 1;

            // --------------------------

            // Calculate Historical Exposure from ATR columns
            const yearsToCheck = [
              2016, 2017, 2018, 2019, 2020, 2021, 2022, 2023, 2024, 2025,
            ];
            let historicalExposureYears = 0;
            yearsToCheck.forEach((year) => {
              const val = parseAtrValue(row[`Tot_PP${year}`]);
              if (val !== null) historicalExposureYears += 1;
            });

            if (historicalExposureYears === 0) {
              historicalExposureYears = exposure || 1;
            }

            // As-If Premium for historical profitability
            const historicalPremium = premium * historicalExposureYears;

            return {
              ...row,
              Targa: row["Targa"],
              Compagnia:
                row["Compagnia"] || row["UltimaCompagnia"] || "Sconosciuta",
              DataInizioCopertura: startDate,
              DataFineCopertura: endDate,
              Totale_Sinistri: claimsCount,
              Esposizione: historicalExposureYears, // Using historical for aggregate logic
              Premio_Annuale: premium,
              HistoricalPremium: historicalPremium,
              Costo_Usato: usedCost,
              Tipo_veicolo_ania: rawType,
              DetailedType: detailedType, // Store detailed type for stats
            };
          })
          .filter((p) => p.Targa && !p.Targa.includes("Targa non trovata"));

        // --- ML Aggregates ---
        const calculateAvg = (arr) =>
          arr.length > 0 ? arr.reduce((a, b) => a + b, 0) / arr.length : 0;

        // Prepare Top Models (Limit to top 30 to save context)
        const topModelli = Object.entries(distModello)
          .map(([m, c]) => ({ modello: m, count: c }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 30);

        const mlEnrichedData = {
          dist_kw: {
            min: kwValues.length > 0 ? Math.min(...kwValues) : 0,
            max: kwValues.length > 0 ? Math.max(...kwValues) : 0,
            avg: parseFloat(calculateAvg(kwValues).toFixed(2)),
          },
          dist_cilindrata: {
            min: ccValues.length > 0 ? Math.min(...ccValues) : 0,
            max: ccValues.length > 0 ? Math.max(...ccValues) : 0,
            avg: parseFloat(calculateAvg(ccValues).toFixed(2)),
          },
          dist_anzianita_veicolo: {
            avg: parseFloat(calculateAvg(vehicleAges).toFixed(1)),
            distribution: ageDistribution, // Updated with real bucket data
          },
          dist_classe_merito: {
            avg: parseFloat(calculateAvg(bonusMalusValues).toFixed(1)),
            distribution: bonusMalusDist,
          },
          dist_uso: usageStats,
          stima_percorrenza_media: Math.round(calculateAvg(mileageEstimates)),
          dist_cap: distCap,
          // New Fields populated from CSV
          dist_alimentazione: distAlimentazione,
          dist_blackbox: distBlackBox,
          dist_carrozzeria: distCarrozzeria,
          top_modelli: topModelli,
        };

        // --- 1. KPI Portafoglio ---
        const totalPolicies = policies.length;
        const totalExposure = policies.reduce(
          (acc, p) => acc + p.Esposizione,
          0
        );
        const totalClaims = policies.reduce(
          (acc, p) => acc + p.Totale_Sinistri,
          0
        );
        const totalCost = policies.reduce((acc, p) => acc + p.Costo_Usato, 0);

        // Current Annual Premium (Snapshot)
        const totalCurrentAnnualPremiums = policies.reduce(
          (acc, p) => acc + p.Premio_Annuale,
          0
        );
        // Historical Cumulative Premium (As-If)
        const totalHistoricalPremiums = policies.reduce(
          (acc, p) => acc + p.HistoricalPremium,
          0
        );

        // Financial Calculations (Historical)
        const lossRatio =
          totalHistoricalPremiums > 0
            ? (totalCost / totalHistoricalPremiums) * 100
            : 0;

        const expenseAmount =
          (totalHistoricalPremiums * expenseRatioInput) / 100;
        const commissionAmount =
          (totalHistoricalPremiums * commissionRatioInput) / 100;
        const totalExpenses = expenseAmount + commissionAmount;

        const combinedCost = totalCost + totalExpenses;
        const combinedRatio =
          totalHistoricalPremiums > 0
            ? (combinedCost / totalHistoricalPremiums) * 100
            : 0;
        const technicalProfit = totalHistoricalPremiums - combinedCost;

        const kpi = {
          tot_polizze: totalPolicies,
          tot_esposizione_anni: parseFloat(totalExposure.toFixed(2)),
          tot_sinistri: totalClaims,
          tot_costo: parseFloat(totalCost.toFixed(2)),
          tot_premi: parseFloat(totalHistoricalPremiums.toFixed(2)), // Cumulative for consistency
          premio_annuale_corrente: parseFloat(
            totalCurrentAnnualPremiums.toFixed(2)
          ), // Snapshot
          premio_medio:
            totalPolicies > 0
              ? parseFloat(
                  (totalCurrentAnnualPremiums / totalPolicies).toFixed(2)
                )
              : 0,
          frequenza_percent:
            totalExposure > 0
              ? parseFloat(((totalClaims / totalExposure) * 100).toFixed(2))
              : 0,
          costo_medio: avgCostSimulation,
          pure_premium:
            totalExposure > 0
              ? parseFloat((totalCost / totalExposure).toFixed(2))
              : 0,
          loss_ratio_percent: parseFloat(lossRatio.toFixed(2)),
          expense_ratio_percent: expenseRatioInput,
          commission_ratio_percent: commissionRatioInput,
          franchigia_media: deductibleInput,
          combined_ratio_percent: parseFloat(combinedRatio.toFixed(2)),
          profitto_tecnico: parseFloat(technicalProfit.toFixed(2)),
        };

        // --- 2. Monthly Frequency & Financial Trend ---
        const monthlyData = {};

        policies.forEach((p) => {
          const date = parseDate(p.DataInizioCopertura);
          if (date) {
            const key = `${date.getFullYear()}-${String(
              date.getMonth() + 1
            ).padStart(2, "0")}`;
            if (!monthlyData[key])
              monthlyData[key] = {
                claims: 0,
                exposure: 0,
                cost: 0,
                earnedPremiumApprox: 0,
              };
            monthlyData[key].claims += p.Totale_Sinistri;
            monthlyData[key].exposure += 1; // Normalized exposure count
            monthlyData[key].cost += p.Costo_Usato;
            // Distribute annual premium based on exposure weight approx
            monthlyData[key].earnedPremiumApprox += p.Premio_Annuale;
          }
        });

        const monthlyFreq = Object.entries(monthlyData)
          .map(([mese, data]) => {
            const monthlyPremium = data.earnedPremiumApprox;
            const monthlyLR =
              monthlyPremium > 0 ? (data.cost / monthlyPremium) * 100 : 0;
            const totalExpenseRatio = expenseRatioInput + commissionRatioInput;
            const monthlyCR =
              monthlyPremium > 0
                ? ((data.cost + (monthlyPremium * totalExpenseRatio) / 100) /
                    monthlyPremium) *
                  100
                : 0;

            return {
              Mese: mese,
              Totale_Sinistri: data.claims,
              Esposizione: parseFloat(data.exposure.toFixed(2)),
              Frequenza_percent:
                data.exposure > 0
                  ? parseFloat(((data.claims / data.exposure) * 100).toFixed(2))
                  : 0,
              Loss_Ratio_percent: parseFloat(monthlyLR.toFixed(1)),
              Combined_Ratio_percent: parseFloat(monthlyCR.toFixed(1)),
            };
          })
          .sort((a, b) => a.Mese.localeCompare(b.Mese));

        // --- 3. Company Stats ---
        const companyData = {};

        policies.forEach((p) => {
          const comp = p.Compagnia;
          if (!companyData[comp])
            companyData[comp] = { claims: 0, exposure: 0, cost: 0, premium: 0 };
          companyData[comp].claims += p.Totale_Sinistri;
          companyData[comp].exposure += p.Esposizione;
          companyData[comp].cost += p.Costo_Usato;
          companyData[comp].premium += p.HistoricalPremium;
        });

        const companyStats = Object.entries(companyData)
          .map(([comp, data]) => ({
            Compagnia: comp,
            Totale_Sinistri: data.claims,
            Esposizione: parseFloat(data.exposure.toFixed(2)),
            Frequenza_percent:
              data.exposure > 0
                ? parseFloat(((data.claims / data.exposure) * 100).toFixed(2))
                : 0,
            Loss_Ratio_percent:
              data.premium > 0
                ? parseFloat(((data.cost / data.premium) * 100).toFixed(2))
                : 0,
            Pure_Premium:
              data.exposure > 0
                ? parseFloat((data.cost / data.exposure).toFixed(2))
                : 0,
            Premio_Annuale: parseFloat(data.premium.toFixed(2)),
            Costo_Usato: parseFloat(data.cost.toFixed(2)),
          }))
          .sort((a, b) => b.Totale_Sinistri - a.Totale_Sinistri);

        // --- 4. Vehicle Type Stats (USING DETAILED TYPES) ---
        const vehicleTypeData = {};

        policies.forEach((p) => {
          // Use detailed type for aggregation
          const type = p.DetailedType;

          if (!vehicleTypeData[type])
            vehicleTypeData[type] = {
              count: 0,
              claims: 0,
              exposure: 0,
              cost: 0,
              premium: 0,
            };

          vehicleTypeData[type].count++;
          vehicleTypeData[type].claims += p.Totale_Sinistri;
          vehicleTypeData[type].exposure += p.Esposizione;
          vehicleTypeData[type].cost += p.Costo_Usato;
          vehicleTypeData[type].premium += p.HistoricalPremium;
        });

        const vehicleStats = Object.entries(vehicleTypeData)
          .map(([type, data]) => ({
            Tipo: type,
            Numero_Polizze: data.count,
            Totale_Sinistri: data.claims,
            Esposizione: parseFloat(data.exposure.toFixed(2)),
            Frequenza_percent:
              data.exposure > 0
                ? parseFloat(((data.claims / data.exposure) * 100).toFixed(2))
                : 0,
            Loss_Ratio_percent:
              data.premium > 0
                ? parseFloat(((data.cost / data.premium) * 100).toFixed(2))
                : 0,
            Premio_Totale: parseFloat(data.premium.toFixed(2)),
            Costo_Totale: parseFloat(data.cost.toFixed(2)),
          }))
          .sort((a, b) => b.Numero_Polizze - a.Numero_Polizze);

        // --- 5. Forecast (UPDATED to use Yearly History) ---
        const effectiveAvgCost = Math.max(
          0,
          avgCostSimulation - deductibleInput
        );

        // Extract Yearly Totals for Prediction
        const yearsToCheck = [
          2016, 2017, 2018, 2019, 2020, 2021, 2022, 2023, 2024, 2025,
        ];
        const yearlyClaimsData = yearsToCheck.map((year) => {
          let total = 0;
          policies.forEach((p) => {
            const val = parseAtrValue(p[`Tot_PP${year}`]);
            if (val !== null) total += val;
          });
          return { year, claims: total };
        });

        const forecast = forecastClaimsFromYearlyHistory(
          yearlyClaimsData,
          12,
          effectiveAvgCost
        );

        // --- 6. Yearly ATR Analysis ---
        const totalExpenseRatio = expenseRatioInput + commissionRatioInput;
        const currentPortfolioAvgPremium = kpi.premio_medio;

        const atrStats = yearsToCheck.map((year) => {
          let observedVehicles = 0;
          let totalClaimsPP = 0;
          let totalClaimsPri = 0;
          let totalClaimsPar = 0;

          policies.forEach((p) => {
            const valPP = parseAtrValue(p[`Tot_PP${year}`]);
            const valPri = parseAtrValue(p[`Tot_ResPri${year}`]);
            const valPar = parseAtrValue(p[`Tot_ResPar${year}`]);

            if (valPP !== null) {
              observedVehicles++;
              totalClaimsPP += valPP;
              if (valPri !== null) totalClaimsPri += valPri;
              if (valPar !== null) totalClaimsPar += valPar;
            }
          });

          const estimatedClaimsCost = totalClaimsPP * effectiveAvgCost;
          const estimatedTotalPremium =
            observedVehicles * currentPortfolioAvgPremium;
          const estimatedExpenses =
            estimatedTotalPremium * (totalExpenseRatio / 100);
          const estimatedTechnicalProfit =
            estimatedTotalPremium - estimatedClaimsCost - estimatedExpenses;

          return {
            Anno: year,
            Veicoli_Osservati: observedVehicles,
            Numero_Sinistri_Totali: totalClaimsPP,
            Frequenza_Totale:
              observedVehicles > 0
                ? parseFloat(
                    ((totalClaimsPP / observedVehicles) * 100).toFixed(2)
                  )
                : 0,
            Numero_Sinistri_Principali: totalClaimsPri,
            Frequenza_Principale:
              observedVehicles > 0
                ? parseFloat(
                    ((totalClaimsPri / observedVehicles) * 100).toFixed(2)
                  )
                : 0,
            Numero_Sinistri_Paritari: totalClaimsPar,
            Frequenza_Paritaria:
              observedVehicles > 0
                ? parseFloat(
                    ((totalClaimsPar / observedVehicles) * 100).toFixed(2)
                  )
                : 0,
            Costo_Sinistri_Totale: parseFloat(estimatedClaimsCost.toFixed(2)),
            Monte_Premi_Stimato: parseFloat(estimatedTotalPremium.toFixed(2)),
            Profitto_Tecnico_Stimato: parseFloat(
              estimatedTechnicalProfit.toFixed(2)
            ),
          };
        });

        resolve({
          kpi_portafoglio: kpi,
          freq_mensile: monthlyFreq,
          stats_compagnia: companyStats,
          stats_tipo_veicolo: vehicleStats,
          forecast: forecast,
          analisi_annuale_atr: atrStats,
          dist_provincia: distProvincia,
          dist_marca: distMarca,
          ml_enriched_data: mlEnrichedData,
        });
      },
      error: (error) => {
        reject(error);
      },
    });
  });
};
